// src/App.jsx — Composition root for the ISM dashboard.
import React, { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import useRequests from './features/tickets/hooks/useRequests';
import useAppInitialization from './shared/hooks/useAppInitialization';

import { AuthProvider, useAuthContext } from './context/AuthContext';
import { ThemeProvider, useThemeContext } from './context/ThemeContext';

import AppLayout from './layouts/AppLayout';
import AuthLayout from './layouts/AuthLayout';
import TweaksPanel from './components/TweaksPanel';
import AppErrorBoundary from './shared/components/AppErrorBoundary';
import { NotificationProvider } from './features/notifications';

import './styles.css';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SettingsRoute = lazy(() => import('./pages/SettingsRoute'));
const LoginScreen = lazy(() => import('./features/auth/components/LoginScreen'));

function RouteLoading() {
  return (
    <div className="app-route-loading" role="status" aria-live="polite">
      Loading view…
    </div>
  );
}

function AuthenticatedApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, logout } = useAuthContext();
  const { tweaks, setTweak } = useThemeContext();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [isNotRelated, setIsNotRelated] = useState(false);
  const [filters, setFilters] = useState({ priority: 'all', type: 'all', assignee: 'all', status: 'all' });
  const [cardFilter, setCardFilter] = useState(() => new URLSearchParams(location.search).get('dashboard_filter') || null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
  const { refreshForNewTickets } = useAppInitialization({
    location, navigate, setRefreshKey, setSummaryRefreshKey,
  });

  const {
    requests,
    setRequests,
    allRequestCount,
    dateRange,
    setDateRange,
    stats,
    sidebarCategories,
    agents,
    ticketCategories,
    ticketStatuses,
    loading,
    error,
    summaryError,
    masterDataStatus,
    retryMasterData,
    pagination,
    loadPage,
    reload,
    refresh,
    fetchAllForExport,
  } = useRequests({ filters, categoryFilter, search, cardFilter, refreshKey, summaryRefreshKey });

  const handleCardFilter = value => {
    setCardFilter(current => {
      const next = current === value ? null : value;
      const params = new URLSearchParams(location.search);
      if (next) params.set('dashboard_filter', next);
      else params.delete('dashboard_filter');
      navigate(`${location.pathname}${params.toString() ? `?${params}` : ''}`, { replace: !next });
      return next;
    });
  };

  const handleCategoryFilter = (id) => {
    setIsNotRelated(false);
    setCategoryFilter(id);
    setFilters(f => ({ ...f, type: 'all' }));
  };

  const handleMasterDataChanged = () => retryMasterData().catch(() => {});

  const dashboardProps = {
    requests,
    setRequests,
    allRequestCount,
    dateRange,
    setDateRange,
    stats,
    sidebarCategories,
    agents,
    ticketCategories,
    ticketStatuses,
    loading,
    error,
    summaryError,
    masterDataStatus,
    retryMasterData,
    pagination,
    loadPage,
    reload,
    refresh,
    fetchAllForExport,
    auth,
    tweaks,
    search,
    categoryFilter,
    isNotRelated,
    filters,
    setFilters,
    cardFilter,
    onCardFilter: handleCardFilter,
    onRefreshForNewTickets: refreshForNewTickets,
  };

  return (
    <NotificationProvider key={auth?.user?.id || auth?.user?.email || auth?.token}>
      <AppLayout
        auth={auth}
        logout={logout}
        sidebarCategories={sidebarCategories}
        onCategoryFilter={handleCategoryFilter}
        onNotRelated={setIsNotRelated}
        isNotRelated={isNotRelated}
        activeCategoryFilter={categoryFilter}
        search={search}
        setSearch={setSearch}
        navigate={navigate}
      >
        <Routes>
          <Route
            path="/overview"
            element={<DashboardPage {...dashboardProps} />}
          />
          <Route
            path="/settings"
            element={<SettingsRoute onCategoriesChanged={handleMasterDataChanged} />}
          />
          <Route path="/tickets/:id" element={<DashboardPage {...dashboardProps} />} />
          <Route path="/" element={<Navigate to="/overview" replace />} />
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </AppLayout>
      <TweaksPanel tweaks={tweaks} setTweak={setTweak} />
    </NotificationProvider>
  );
}

function AppRoutes() {
  const { auth, login, sessionExpired } = useAuthContext();
  const { appClass, themeStyle } = useThemeContext();
  const location = useLocation();

  return (
    <div className={appClass} style={themeStyle}>
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route path="/login" element={
            auth
              ? <Navigate to={location.state?.from || '/overview'} replace />
              : <AuthLayout>
                  <LoginScreen
                    onLogin={data => login(data)}
                    sessionExpired={sessionExpired}
                  />
                </AuthLayout>
          } />
          <Route path="/*" element={
            !auth
              ? <Navigate to="/login" replace state={{ from: location }} />
              : <AuthenticatedApp />
          } />
        </Routes>
      </Suspense>
    </div>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <AuthProvider>
            <ThemeProvider>
              <AppRoutes />
            </ThemeProvider>
          </AuthProvider>
        </LocalizationProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}
